import React, { useMemo, useReducer } from 'react';
import cn from 'classnames';
import { ResponsiveLine } from '@nivo/line';
import { Serie } from '@nivo/line';
import { formatCurrency } from 'apps/sigma2-ui/src/lib/number-formatting';
import { useDimensionConfig } from 'apps/sigma2-ui/src/lib/hooks/useDimensionConfig';
import { Color } from 'libs/end-to-end/src/types/branding/color';
import { getBubbleColor } from 'apps/sigma2-ui/src/pay-correction/components/pay-grade-graph-utils';
import { getBubbleCountText } from 'apps/sigma2-ui/src/pay-correction/components/pay-grade-graph-utils';
import { getBubbleSizeInPx } from 'apps/sigma2-ui/src/pay-correction/components/pay-grade-graph-utils';
import { PayGradeGraphDatum } from 'apps/sigma2-ui/src/pay-correction/components/pay-grade-graph-utils';
import { roundToStep } from 'apps/sigma2-ui/src/pay-correction/components/pay-grade-graph-utils';
import { SELECTED_BORDER_OFFSET } from 'apps/sigma2-ui/src/pay-correction/components/pay-grade-graph-utils';

const PayGroupBubble: React.FC<{
  datum: PayGradeGraphDatum;
  graphMin: number | null;
  graphMax: number | null;
  roundedInternalGoalMin: number | null;
  roundedInternalGoalMax: number | null;
  levelToColor: { [demographicKey: string]: Color };
  isSelected: boolean;
  highlightedDimensionLevel: string | null;
  highlightedAdjustedSalary: null | number;
  employeeCount: number;
  adjustedSalaryFilters: number[];
}> = ({
  datum,
  roundedInternalGoalMin,
  roundedInternalGoalMax,
  graphMin,
  graphMax,
  levelToColor,
  isSelected,
  highlightedDimensionLevel,
  highlightedAdjustedSalary,
  employeeCount,
  adjustedSalaryFilters,
}) => {
  const size = getBubbleSizeInPx({
    thisBubbleEmployeeCount: datum.count,
    totalEmployeeCount: employeeCount,
  });

  const isLessThanInternalGoal = !!(
    roundedInternalGoalMin && datum.adjustedSalary < roundedInternalGoalMin
  );

  const hasReducedOpacity = !!(
    highlightedAdjustedSalary &&
    datum.adjustedSalary !== highlightedAdjustedSalary
  );

  const fill = getBubbleColor({
    variant: 'fill',
    levelToColor,
    highlightedDimensionLevel: highlightedDimensionLevel,
    countByLevel: datum.countByLevel,
    isLessThanInternalGoal,
    hasReducedOpacity,
    adjustedSalaryFilters,
    datum,
  });

  const stroke = getBubbleColor({
    variant: 'stroke',
    levelToColor,
    highlightedDimensionLevel: highlightedDimensionLevel,
    countByLevel: datum.countByLevel,
    isLessThanInternalGoal: isLessThanInternalGoal,
    hasReducedOpacity,
    adjustedSalaryFilters,
    datum,
  });

  const selectedStroke = getBubbleColor({
    variant: 'selectedStroke',
    levelToColor,
    highlightedDimensionLevel: highlightedDimensionLevel,
    countByLevel: datum.countByLevel,
    isLessThanInternalGoal: isLessThanInternalGoal,
    hasReducedOpacity,
    adjustedSalaryFilters,
    datum,
  });

  const isInternalGoalBoundaryMarker = [
    roundedInternalGoalMin,
    roundedInternalGoalMax,
  ].includes(datum.adjustedSalary);

  const isGraphBoundaryMarker = [graphMin, graphMax].includes(
    datum.adjustedSalary,
  );

  const isBoundaryMarker =
    isInternalGoalBoundaryMarker || isGraphBoundaryMarker;

  // TODO see why the bubble with zero items is larger than the one with 3 items
  if (isBoundaryMarker) {
    return (
      <g>
        <path
          xmlns="http://www.w3.org/2000/svg"
          d="M0 -20L0 20"
          strokeLinecap="round"
          className={cn('stroke-current', {
            'text-black stroke-[3]': isInternalGoalBoundaryMarker,
            'text-gray-400 stroke-[2]': isGraphBoundaryMarker,
          })}
        />
      </g>
    );
  }

  return (
    <g>
      {/* Outer ring shown when selected */}
      {isSelected && (
        <circle
          r={size + SELECTED_BORDER_OFFSET}
          className={selectedStroke}
        ></circle>
      )}

      {/* Filled circle with numbers */}
      <circle r={size} className={fill}></circle>

      {/* Border circle */}
      <circle r={size} className={stroke}></circle>

      {/* Count of employees in this pay band */}
      {datum.count > 1 && (
        <text
          className="fill-current text-white"
          textAnchor="middle"
          alignmentBaseline="middle"
        >
          {getBubbleCountText({
            highlightedDimensionLevel,
            datum,
            adjustedSalaryFilters,
          })}
        </text>
      )}
    </g>
  );
};

const TICK_MARGIN = 25;

export const PayGradeGraph: React.FC<{
  data: Serie[];
  roundedGraphMin: number | null;
  roundedGraphMax: number | null;
  roundedInternalGoalMin: number | null;
  roundedInternalGoalMax: number | null;
  highlightedDimensionLevel: string | null;
  onAdjustedSalaryFilterChange: (adjustedSalary: number) => void;
  adjustedSalaryFilters: number[];
  step: number;
  employeeCount: number;
}> = ({
  data,
  roundedGraphMin,
  roundedGraphMax,
  roundedInternalGoalMin,
  roundedInternalGoalMax,
  highlightedDimensionLevel,
  adjustedSalaryFilters,
  step,
  employeeCount,
  onAdjustedSalaryFilterChange,
}) => {
  const { allLevels } = useDimensionConfig();

  const levelToColor = useMemo(
    () =>
      allLevels.reduce((acc: { [level: string]: Color }, x) => {
        acc[x.columnValue] = x.color;
        return acc;
      }, {}),
    [allLevels],
  );

  const [highlightedAdjustedSalary, dispatchHighlightedAdjustedSalary] =
    useReducer(
      (
        _state: null | number /* adjustedSalary */,
        action:
          | { type: 'HIGHLIGHT'; adjustedSalary: number }
          | { type: 'CLEAR_HIGHLIGHT' },
      ) => {
        if (action.type === 'HIGHLIGHT') {
          return roundToStep({ step, value: action.adjustedSalary });
        }

        return null;
      },
      null,
    );

  return (
    <div className="h-56 w-full">
      <ResponsiveLine
        data={data}
        colors={{ scheme: 'greys', datum: 'color' }}
        margin={{ top: 50, right: 110, bottom: 50, left: 60 }}
        axisRight={null}
        enableGridX={false}
        xScale={{ type: 'point' }}
        axisTop={{
          tickSize: 5,
          tickRotation: 0,
          renderTick: (tick) => {
            const adjustedSalary = tick.value;

            const visibleTicks = new Set([
              roundedInternalGoalMin,
              roundedInternalGoalMax,
            ]);

            if (visibleTicks.has(adjustedSalary)) {
              return (
                <g transform={`translate(${tick.x},${tick.y - TICK_MARGIN})`}>
                  <text
                    textAnchor="middle"
                    dominantBaseline="middle"
                    style={{
                      fontSize: 20,
                      fontWeight: 700,
                    }}
                    fill="#333333"
                  >
                    {formatCurrency(adjustedSalary)}
                  </text>
                </g>
              );
            }

            return <></>;
          },
        }}
        axisBottom={{
          tickSize: 5,
          tickPadding: 5,
          tickRotation: 0,
          renderTick: (tick) => {
            const adjustedSalary = tick.value;

            const visibleTicks = new Set([roundedGraphMin, roundedGraphMax]);

            if (visibleTicks.has(adjustedSalary)) {
              return (
                <g transform={`translate(${tick.x},${tick.y + TICK_MARGIN})`}>
                  <text
                    textAnchor="middle"
                    dominantBaseline="middle"
                    style={{
                      fontSize: 20,
                      fontWeight: 700,
                    }}
                    fill="#696D70"
                  >
                    {formatCurrency(adjustedSalary)}
                  </text>
                </g>
              );
            }

            return <></>;
          },
        }}
        xFormat={(x) => {
          const adjustedSalary = Number(x);
          // TODO add actual range this number is between, i.e. 100,000 - 150,000 so it's more explicit. We need the step prop for this
          return '~' + formatCurrency(adjustedSalary);
        }}
        axisLeft={{
          renderTick: () => {
            return <></>;
          },
        }}
        enablePoints
        onClick={(x) => {
          onAdjustedSalaryFilterChange(Number(x.data.x));
        }}
        onMouseMove={(x) => {
          dispatchHighlightedAdjustedSalary({
            type: 'HIGHLIGHT',
            adjustedSalary: Number(x.data.x),
          });
        }}
        onMouseLeave={() => {
          dispatchHighlightedAdjustedSalary({ type: 'CLEAR_HIGHLIGHT' });
        }}
        lineWidth={3}
        pointSymbol={(x) => {
          const xDatum = x.datum as PayGradeGraphDatum;

          return (
            <PayGroupBubble
              {...x}
              datum={xDatum}
              roundedInternalGoalMin={roundedInternalGoalMin}
              roundedInternalGoalMax={roundedInternalGoalMax}
              graphMin={roundedGraphMin}
              graphMax={roundedGraphMax}
              highlightedDimensionLevel={highlightedDimensionLevel}
              employeeCount={employeeCount}
              levelToColor={levelToColor}
              isSelected={adjustedSalaryFilters.includes(xDatum.adjustedSalary)}
              highlightedAdjustedSalary={highlightedAdjustedSalary}
              adjustedSalaryFilters={adjustedSalaryFilters}
            />
          );
        }}
        useMesh={true}
      />
    </div>
  );
};
